import type { JobRisk } from "openapi/api/model"

/**
 * How a recruiting desk is failing, from the job-risk rows — the Morning Desk
 * dashboard's whole argument, as pure arithmetic.
 *
 * A desk fails in exactly three ways, on three different clocks:
 *
 * - **starving** — an open job with nobody in it, or nobody who has reached a
 *   submit. Bites in four to six weeks, and is invisible on a page that only
 *   shows what is moving. Most ATS dashboards never surface it.
 * - **stuck** — candidates sitting in a stage while nothing happens. Bites in
 *   one to two weeks. This is the one every competitor shows.
 * - **leaking** — rejections piling up at a single stage, which means the brief
 *   is wrong rather than the pipeline being thin. Chronic.
 *
 * Starving outranks stuck deliberately, and it is counterintuitive: a stuck job
 * feels more urgent because it has a number attached. But a stuck job recovers
 * with one phone call, and a job with nobody in it is already weeks from
 * failing. Surfacing the failure you cannot yet feel is the page's reason to
 * exist.
 *
 * Pure and separate from anything that draws, so the classification can be
 * tested against rows rather than against a screenshot.
 */

/** Rejections at or above this share of everyone screened reads as leakage. */
const LEAK_SHARE = 0.6
/** Below this many rejections the share is noise, not a pattern. */
const LEAK_FLOOR = 5
/** Quiet for this many days is stuck. */
const STUCK_DAYS = 7

export type Trouble = "starving" | "stuck" | "leaking"

export type DeskJob = {
  id: string
  title: string
  company?: string
  recruiter?: string
  trouble: Trouble
  /** The mono line under the title — why this row is here, in figures. */
  why: string
  /** What the row's action says, in the imperative. */
  action: string
  /** Days since anything happened, where the row knows. */
  quietFor?: number
  /** Ranked within its group: bigger is worse. */
  weight: number
  /** The pipeline, in the order a pipeline runs. */
  shape: { stage: string; count: number }[]
  live: number
}

const count = (value: number | null | undefined) => (typeof value === "number" ? value : 0)

/** Days since a timestamp, or undefined when there is no usable one. */
export function daysSince(at?: string | null, now = Date.now()) {
  const parsed = Date.parse(at ?? "")
  if (!Number.isFinite(parsed)) return undefined
  return Math.max(0, Math.floor((now - parsed) / 86_400_000))
}

/**
 * The stages, in pipeline order.
 *
 * `rejected` and `hired` are deliberately absent: neither is a place a
 * candidate is waiting, so neither belongs in the bar that says whether anyone
 * is moving. Rejections come back in the leak test instead.
 */
export function shapeOf(job: JobRisk) {
  return [
    { stage: "longlist", count: count(job.longlist_count) },
    { stage: "shortlist", count: count(job.shortlist_count) },
    { stage: "outbound", count: count(job.outbound_count) },
    { stage: "screening", count: count(job.screening_count) },
    { stage: "submitted", count: count(job.submitted_count) },
    { stage: "interviewing", count: count(job.interviewing_count) },
  ]
}

/** Everyone still in play — the count that decides whether a job is starving. */
export function liveCount(job: JobRisk) {
  return shapeOf(job).reduce((sum, stage) => sum + stage.count, 0)
}

/**
 * Which way this job is failing, or nothing at all.
 *
 * Order matters: a job with nobody in it is starving even if its handful of
 * rejections would also read as a leak, because sourcing is what it needs.
 */
export function troubleWith(job: JobRisk, now = Date.now()): Trouble | undefined {
  if (job.status && job.status !== "active") return undefined

  const live = liveCount(job)
  const reachedSubmit = count(job.submitted_count) + count(job.interviewing_count)
  if (live === 0 || reachedSubmit === 0) return "starving"

  const quiet =
    typeof job.inactivity_duration === "number"
      ? job.inactivity_duration
      : daysSince(job.last_activity_at, now)
  if (quiet !== undefined && quiet >= STUCK_DAYS) return "stuck"

  const rejected = count(job.rejected_count)
  const screened = rejected + count(job.screening_count) + reachedSubmit
  if (rejected >= LEAK_FLOOR && screened > 0 && rejected / screened >= LEAK_SHARE) {
    return "leaking"
  }

  return undefined
}

/** The imperative each kind of trouble answers to. */
const ACTION: Record<Trouble, string> = {
  starving: "Source",
  stuck: "Chase",
  leaking: "Review brief",
}

function why(job: JobRisk, trouble: Trouble, quiet: number | undefined, live: number, now = Date.now()) {
  const open = daysSince(job.job_created_at, now)
  const openFor = open === undefined ? undefined : `${open}d open`

  if (trouble === "starving") {
    const body = live === 0 ? "nobody in the pipeline" : `${live} sourced · 0 submitted`
    return [openFor, body].filter(Boolean).join(" · ")
  }
  if (trouble === "stuck") {
    return [quiet === undefined ? undefined : `quiet ${quiet}d`, `${live} in play`]
      .filter(Boolean)
      .join(" · ")
  }
  const rejected = count(job.rejected_count)
  const screened = rejected + count(job.screening_count) + count(job.submitted_count)
  return `${rejected} of ${screened} rejected at screen`
}

/**
 * One job's row, or nothing when the job is fine.
 *
 * `weight` ranks within a group rather than across them — the groups are
 * already ordered by which clock is shortest, so comparing a starving job's
 * days-open against a stuck job's days-quiet would be comparing two different
 * units and calling the bigger number worse.
 */
export function deskRowFor(job: JobRisk, now = Date.now()): DeskJob | undefined {
  const trouble = troubleWith(job, now)
  if (!trouble) return undefined

  const live = liveCount(job)
  const quiet =
    typeof job.inactivity_duration === "number"
      ? job.inactivity_duration
      : daysSince(job.last_activity_at, now)

  const weight =
    trouble === "starving"
      ? (daysSince(job.job_created_at, now) ?? 0)
      : trouble === "stuck"
        ? (quiet ?? 0)
        : count(job.rejected_count)

  return {
    id: job.job_id,
    title: job.title,
    company: job.company_name ?? undefined,
    recruiter: job.recruiter_name ?? undefined,
    trouble,
    why: why(job, trouble, quiet, live, now),
    action: ACTION[trouble],
    quietFor: quiet,
    weight,
    shape: shapeOf(job),
    live,
  }
}

export type DeskGroup = { trouble: Trouble; rows: DeskJob[] }

/** Starving, then stuck, then leaking — shortest fuse last to be felt, first shown. */
const GROUP_ORDER: Trouble[] = ["starving", "stuck", "leaking"]

export function groupDesk(jobs: JobRisk[], now = Date.now()): DeskGroup[] {
  const rows = jobs
    .map((job) => deskRowFor(job, now))
    .filter((row): row is DeskJob => row !== undefined)

  return GROUP_ORDER.map((trouble) => ({
    trouble,
    rows: rows
      .filter((row) => row.trouble === trouble)
      .sort((a, b) => b.weight - a.weight),
  })).filter((group) => group.rows.length > 0)
}

/**
 * The line under the claim: what the desk is, in figures the claim is derived
 * from. Every number here is one the reader can check against the rows.
 */
export function deskSummary(jobs: JobRisk[], now = Date.now()) {
  const active = jobs.filter((job) => !job.status || job.status === "active")
  const groups = groupDesk(active, now)
  const of = (trouble: Trouble) =>
    groups.find((group) => group.trouble === trouble)?.rows.length ?? 0

  return {
    open: active.length,
    starving: of("starving"),
    stuck: of("stuck"),
    leaking: of("leaking"),
    healthy: active.length - groups.reduce((sum, group) => sum + group.rows.length, 0),
  }
}
